import React from 'react';
import { Link } from 'react-router-dom';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'text' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  to?: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  to,
  onClick,
  className = '',
  disabled = false,
  type = 'button'
}) => {
  const [isPressed, setIsPressed] = React.useState(false);

  React.useEffect(() => {
    if (disabled) {
      setIsPressed(false);
    }
  }, [disabled]);

  const baseClasses = 'font-nunito rounded-full inline-flex items-center justify-center focus:outline-none select-none touch-manipulation transition-[transform,background-color,box-shadow,opacity] duration-300 ease-out active:duration-150 active:ease-in will-change-transform';
  
  const variantClasses = {
    primary: 'bg-brand text-neutral-offwhite hover:bg-brand-dark focus-visible:ring-2 focus-visible:ring-brand-light shadow-sm active:bg-brand-dark/90',
    secondary: 'bg-accent-rose text-neutral-ink hover:bg-accent-sand focus-visible:ring-2 focus-visible:ring-accent-rose shadow-sm active:bg-accent-sand/90',
    outline: 'bg-transparent border-2 border-brand text-brand hover:bg-brand/10 focus-visible:ring-2 focus-visible:ring-brand-light active:bg-brand/15',
    text: 'bg-transparent text-brand hover:bg-brand/5 active:bg-brand/10',
    ghost: 'bg-transparent text-brand hover:bg-brand/5 focus-visible:ring-2 focus-visible:ring-brand-light active:bg-brand/10'
  } as const;

  const sizeClasses = {
    sm: 'text-sm px-4 py-1.5',
    md: 'text-base px-6 py-2',
    lg: 'text-lg px-8 py-3'
  };

  const disabledClasses = disabled ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'cursor-pointer';

  const pressedClasses = isPressed ? 'scale-[0.97] translate-y-[1px] shadow-inner' : '';

  const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${pressedClasses} ${className}`;

  const triggerAction = () => {
    if (disabled) {
      return;
    }

    if (to) {
      window.scrollTo(0, 0);
    }

    onClick?.();
  };

  const handlePressStart = () => {
    if (!disabled) {
      setIsPressed(true);
    }
  };

  const handlePressEnd = () => {
    if (!disabled) {
      setIsPressed(false);
    }
  };

  const handlePressCancel = () => {
    if (!disabled) {
      setIsPressed(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (disabled) {
      return;
    }

    const isActivationKey = event.key === ' ' || event.key === 'Enter';

    if (!isActivationKey) {
      return;
    }

    if (to && event.key === ' ') {
      event.preventDefault();
    }

    setIsPressed(true);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
    if (disabled) {
      return;
    }

    const isActivationKey = event.key === ' ' || event.key === 'Enter';

    if (!isActivationKey) {
      return;
    }

    if (to && event.key === ' ') {
      event.preventDefault();
      triggerAction();
    }

    setIsPressed(false);
  };
  
  if (to) {
    return (
      <Link
        to={to}
        className={buttonClasses}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          triggerAction();
        }}
        onPointerDown={handlePressStart}
        onPointerUp={handlePressEnd}
        onPointerLeave={handlePressCancel}
        onPointerCancel={handlePressCancel}
        onBlur={handlePressCancel}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        data-pressed={isPressed}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
      >
        {children}
      </Link>
    );
  }
  
  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        triggerAction();
      }}
      disabled={disabled}
      onPointerDown={handlePressStart}
      onPointerUp={handlePressEnd}
      onPointerLeave={handlePressCancel}
      onPointerCancel={handlePressCancel}
      onBlur={handlePressCancel}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      data-pressed={isPressed}
    >
      {children}
    </button>
  );
};
export default Button;